import { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useCashierConfiguration } from '@cashier/cashier-common';
import {
  Directions,
  IntegrationThemes,
  PaymentMethodInput,
  WindowNames,
} from '@cashier/cashier-model';
import {
  AmountConverter,
  ProviderWindowService,
  useUIConfiguration,
} from '@cashier/cashier-shared';
import { Button, PlainHtmlContainer } from '@cashier/cashier-ui';

import { InformationBlockContentWrapper } from '../../informationBlockContent';

import styles from './styles.module.scss';

interface InformationBlockProps {
  amount: string;
  paymentMethodMessage: string | null;
  fields: any;
  onButtonClick: () => void;
  currency: string;
  type: 'shortened' | 'standard';
}

export const InformationBlock = ({
  amount,
  paymentMethodMessage,
  fields,
  onButtonClick,
  currency,
  type,
}: InformationBlockProps): ReactElement => {
  const { direction } = useCashierConfiguration();
  const { t } = useTranslation();

  const buttonType = useUIConfiguration('button', 'type');
  const buttonBackground = useUIConfiguration('button', 'background');

  useEffect(() => {
    ProviderWindowService.close(WindowNames.Window);
  }, []);

  return (
    <div className={styles.flexWrapper}>
      <div className={styles.content} dir="ltr">
        {amount && (
          <div className={styles.amount}>
            {direction === Directions.Rtl && currency}{' '}
            {AmountConverter.exponentialNotationToNumber(
              String(AmountConverter.toDelimitedNumber(amount || 0)),
            )}{' '}
            {direction === Directions.Ltr && currency}
          </div>
        )}

        <div className={styles.info} data-test="display-step-info">
          {t('displayFinalScreen.text1')} <br /> {t('displayFinalScreen.text2')}
        </div>

        {paymentMethodMessage && (
          <PlainHtmlContainer content={paymentMethodMessage} />
        )}

        {fields.map((field: PaymentMethodInput) => (
          <InformationBlockContentWrapper
            key={field.name}
            field={field}
            theme={IntegrationThemes.Platform}
          />
        ))}
      </div>
      {type !== 'shortened' && (
        <>
          <div className={styles.delimiter}>- {t('completePayment.or')} -</div>
          <div
            className={styles.button}
            data-test="make-new-transaction-button"
          >
            <Button
              textSize="small"
              onClick={onButtonClick}
              backgroundType={buttonBackground}
              type={buttonType}
            >
              {t('makeNewTransaction.button')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
